import "react-image-crop/dist/ReactCrop.css";

import { CropIcon, Trash2Icon } from "lucide-react";
import React from "react";
import ReactCrop, { type Crop, type PixelCrop } from "react-image-crop";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/components";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";
import { cn } from "@/lib/utils";

interface ImageCropperProps {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFile: File;
  className?: string;
  fallbackName?: string;
  triggerComponent?: React.ReactNode;
  onCropComplete?: (croppedImage: File) => void;
}

export interface ImageCropperRef {
  openCropper: () => void;
}

export function ImageCropper({
  dialogOpen,
  setDialogOpen,
  className,
  fallbackName,
  triggerComponent,
  selectedFile,
  onCropComplete,
}: ImageCropperProps) {
  const { t } = useI18n();
  const { toast } = useToast();

  const imgRef = React.useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = React.useState<Crop>();
  const [completedCrop, setCompletedCrop] = React.useState<PixelCrop>();
  const [croppedImageUrl, setCroppedImageUrl] = React.useState<string>("");
  const [croppedImage, setCroppedImage] = React.useState<string>("");

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  const fileSrc = React.useMemo(
    () => URL.createObjectURL(selectedFile),
    [selectedFile]
  );

  React.useEffect(() => {
    return () => {
      setCroppedImage("");
      setCroppedImageUrl("");
      URL.revokeObjectURL(fileSrc);
    };
  }, [fileSrc]);

  function onImageLoad() {
    const initialCrop: Crop = {
      unit: "%",
      x: 25,
      y: 25,
      width: 50,
      height: 30,
    };
    setCrop(initialCrop);
  }

  React.useEffect(() => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      const croppedImageUrl = getCroppedImg(imgRef.current, completedCrop);
      setCroppedImageUrl(croppedImageUrl);
    }
  }, [completedCrop]);

  function getCroppedImg(image: HTMLImageElement, crop: PixelCrop): string {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width * scaleX,
        crop.height * scaleY
      );
    }

    return canvas.toDataURL("image/png", 1.0);
  }

  async function onCrop() {
    try {
      setCroppedImage(croppedImageUrl);
      setDialogOpen(false);
      onCropComplete?.(dataURLToFile(croppedImageUrl, selectedFile.name));
    } catch (error: unknown) {
      toast({
        title: t("generic.error"),
        description: t("generic.error"),
        variant: "destructive",
      });
    }
  }

  const defaultTrigger = (
    <div className="relative flex flex-col cursor-pointer items-center justify-center mt-2">
      <Avatar className={cn("size-26", className)}>
        <AvatarImage
          src={croppedImage ? croppedImage : fileSrc}
          alt={selectedFile?.name}
          className="object-cover"
        />
        <AvatarFallback>
          {fallbackName?.charAt(0).toUpperCase() || "U"}
        </AvatarFallback>
      </Avatar>
      <div className="text-xs text-muted-foreground flex gap-1 mt-2">
        <CropIcon className="size-4 text-foreground" />
        {t("components.uploadAvatar.clickToCrop")}
      </div>
    </div>
  );

  return (
    <>
      {/* Renderiza o component sem o Dialog quando aberto externamente */}
      {dialogOpen && !triggerComponent && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="p-0 gap-0 max-w-3xl w-[80vw] h-auto items-center justify-center">
            <div className="p-4 w-full flex">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                keepSelection={true}
                className="w-full flex justify-center"
              >
                <img
                  ref={imgRef}
                  className="w-auto object-contain aspect-square"
                  alt="Image to crop"
                  src={fileSrc}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
            <DialogFooter className="p-4 pt-0 justify-center">
              <DialogClose asChild>
                <Button
                  size={"sm"}
                  type="reset"
                  className="w-fit"
                  variant={"outline"}
                  onClick={() => {}}
                >
                  <Trash2Icon className="mr-1.5 size-4" />
                  {t("generic.cancel")}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                size={"sm"}
                className="w-fit"
                onClick={onCrop}
                disabled={!completedCrop?.width || !completedCrop?.height}
              >
                <CropIcon className="mr-1.5 size-4" />
                {t("generic.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Renderiza o Dialog com trigger quando um trigger é fornecido */}
      {triggerComponent && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>{triggerComponent}</DialogTrigger>
          <DialogContent className="p-0 gap-0 max-w-3xl w-[80vw] h-auto">
            <div className="p-4 w-full flex justify-center items-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                keepSelection={true}
                className="w-full flex justify-center"
              >
                <img
                  ref={imgRef}
                  className="w-auto max-h-[50vh] object-contain"
                  alt="Image to crop"
                  src={fileSrc}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
            <DialogFooter className="p-4 pt-0 justify-center">
              <DialogClose asChild></DialogClose>
              <Button
                type="submit"
                size={"sm"}
                className="w-fit"
                onClick={onCrop}
                disabled={!completedCrop?.width || !completedCrop?.height}
              >
                <CropIcon className="mr-1.5 size-4" />
                {t("generic.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Renderiza o Dialog com o trigger padrão quando nenhum trigger é fornecido e não está aberto externamente */}
      {!triggerComponent && !dialogOpen && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger>{defaultTrigger}</DialogTrigger>
          <DialogContent className="p-0 gap-0 max-w-3xl w-[80vw] h-auto">
            <div className="p-4 w-full flex justify-center items-center">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                keepSelection={true}
                className="w-full flex justify-center"
              >
                <img
                  ref={imgRef}
                  className="w-auto max-h-[50vh] object-contain"
                  alt="Image to crop"
                  src={fileSrc}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
            <DialogFooter className="p-4 pt-0 justify-center">
              <DialogClose asChild>
                <Button
                  size={"sm"}
                  type="reset"
                  className="w-fit"
                  variant={"outline"}
                  onClick={() => {}}
                >
                  <Trash2Icon className="mr-1.5 size-4" />
                  {t("generic.cancel")}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                size={"sm"}
                className="w-fit"
                onClick={onCrop}
                disabled={!completedCrop?.width || !completedCrop?.height}
              >
                <CropIcon className="mr-1.5 size-4" />
                {t("generic.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export const ImageCropperWithRef = React.forwardRef<
  ImageCropperRef,
  Omit<ImageCropperProps, "dialogOpen" | "setDialogOpen">
>((props, ref) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    openCropper: () => setDialogOpen(true),
  }));

  return (
    <ImageCropper
      {...props}
      dialogOpen={dialogOpen}
      setDialogOpen={setDialogOpen}
    />
  );
});
